import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';

const { FiMapPin, FiUsers, FiTrendingUp, FiShield, FiClock, FiAward, FiCheck } = FiIcons;

const About = () => {
  const values = [
    {
      icon: FiShield,
      title: 'Technical excellence',
      description: 'Over a decade of expertise in delivering reliable, high-performance digital solutions that businesses can depend on.'
    },
    {
      icon: FiUsers,
      title: 'Local expertise',
      description: 'Based in South Africa, we understand the unique challenges and opportunities facing local businesses in the digital landscape.'
    },
    {
      icon: FiTrendingUp,
      title: 'Business acumen',
      description: 'We combine technical knowledge with business understanding to deliver solutions that drive growth and success.'
    },
    {
      icon: FiClock,
      title: 'Reliable support',
      description: '24/7 technical support and proactive monitoring ensure your digital infrastructure stays online and performs optimally.'
    }
  ];

  const achievements = [
    { number: '10+', label: 'Years of expertise' },
    { number: '500+', label: 'Satisfied clients' },
    { number: '99.9%', label: 'Uptime achieved' },
    { number: '24/7', label: 'Support availability' }
  ];

  const teamValues = [
    'Integrated business expertise with technical excellence',
    'Comprehensive security and performance optimisation',
    'Local understanding of South African market needs',
    'Proactive support and maintenance approach',
    'Transparent communication and no hidden fees',
    'Scalable solutions that grow with your business'
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="pt-20"
    >
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Combining technical expertise with{' '}
                <span style={{ color: '#FF5841' }}>business understanding</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                For over a decade, Primehost has been delivering reliable digital solutions to South African businesses. Based in Waterfall, Midrand, we understand the unique needs of local businesses and provide the technical excellence they deserve.
              </p>
              <div className="flex items-center space-x-4 text-gray-600">
                <SafeIcon icon={FiMapPin} className="w-5 h-5 text-primary-500" />
                <span>22 Magwa Crescent, Waterfall, Midrand, Gauteng, 1662</span>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <img
                src="https://media.phst.co.za/file/ph/primehost-about-expertise.jpg"
                alt="Professional team collaboration and technical expertise"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-lg">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary-500">10+</div>
                  <div className="text-sm text-gray-600">Years of expertise</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-8">
              Our mission
            </h2>
            <div className="max-w-4xl mx-auto">
              <p className="text-2xl text-gray-700 leading-relaxed font-light">
                "To elevate South African businesses through digital solutions that combine striking design with rock-solid technical performance, delivering reliable infrastructure that businesses can depend on for their growth and success."
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img
                src="https://media.phst.co.za/file/ph/primehost-about-our-story.jpg"
                alt="Modern office environment and professional workspace"
                className="rounded-2xl shadow-xl"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Our story
              </h2>
              <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
                <p>
                  Founded with a vision to bridge the gap between technical excellence and business understanding, Primehost has grown from a small local operation to a trusted partner for hundreds of South African businesses.
                </p>
                <p>
                  Our journey began with a simple belief: that every business deserves reliable, high-performance digital infrastructure without the complexity. Over the years, we have refined our approach, expanded our services, and deepened our expertise.
                </p>
                <p>
                  Today, we are proud to serve businesses across South Africa from our base in Waterfall, Midrand, combining local insight with international standards to deliver solutions that truly make a difference.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Our achievements
            </h2>
            <p className="text-xl text-gray-600">
              Numbers that reflect our commitment to excellence and client success
            </p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {achievements.map((achievement, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl lg:text-5xl font-bold text-primary-500 mb-2">
                  {achievement.number}
                </div>
                <div className="text-gray-600 font-medium">
                  {achievement.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Our values
            </h2>
            <p className="text-xl text-gray-600">
              The principles that guide everything we do
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-2xl shadow-sm"
              >
                <div className="flex items-start space-x-4">
                  <div className="bg-primary-100 p-3 rounded-xl">
                    <SafeIcon icon={value.icon} className="w-6 h-6 text-primary-500" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      {value.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Why choose Primehost?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                We are more than just a hosting provider – we are your technology partner, committed to your success through reliable infrastructure and exceptional support.
              </p>
              <div className="space-y-4">
                {teamValues.map((value, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start space-x-3"
                  >
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mt-0.5" />
                    <span className="text-gray-700">{value}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <img
                src="https://media.phst.co.za/file/ph/primehost-about-why-us.jpg"
                alt="Professional team meeting and strategic planning"
                className="rounded-2xl shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" style={{ backgroundColor: '#D14932' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Ready to work with us?
            </h2>
            <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
              Experience the difference that technical excellence and local expertise can make for your business. Let us build something great together.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-white text-red-700 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
              >
                Get in touch
              </Link>
              <Link
                to="/services"
                className="border border-red-300 text-white px-8 py-4 rounded-lg font-semibold hover:bg-red-600 transition-colors duration-200"
              >
                View our services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default About;